/**
 * CaseSelect
 *
 * Reusable async-paginated, searchable dropdown for selecting cases and leads.
 * Mirrors the EventSelect pattern — wraps AsyncPaginate with the
 * loadCaseSearchOptions loader and shared AsyncSelect styles.
 *
 * Data source: POST /search-cases-by-title (OpenSearch, fuzzy-matched)
 *
 * Search behaviour:
 *  - Fully searchable; the typed query is forwarded to the API as `query`.
 *  - Results reset to page 1 on each new search term.
 *  - Debounced 300 ms to avoid hammering the API on every keystroke.
 *
 * Multi-select behaviour (when isMulti=true):
 *  - Each option shows a checkbox to communicate multi-select affordance.
 *  - Selected items are shown as scrollable chips in the control.
 *  - The menu stays open while selecting (closeMenuOnSelect=false).
 *  - Selected options remain visible in the list (hideSelectedOptions=false).
 *
 * Option display:
 *  - Primary label: case_name
 *  - Secondary badge: case_number (when present)
 *  - Type badge: "Lead" pill shown when is_lead=true
 *  - Closed badge: "Closed" pill shown when case_closed=true
 *
 * @param {Object}            props
 * @param {Object|Array|null} props.value           - Selected value(s); full option object(s)
 * @param {Function}          props.onChange         - Change handler; receives full option object(s)
 * @param {string}            [props.placeholder]    - Input placeholder text (default: 'Search cases...')
 * @param {boolean}           [props.isClearable]    - Show clear button (default: true)
 * @param {boolean}           [props.isDisabled]     - Disable the control (default: false)
 * @param {boolean}           [props.isMulti]        - Enables multi-select mode (default: false)
 * @param {string}            [props.className]      - Additional CSS class on the root element
 * @param {number}            [props.pageSize]       - Records fetched per page (default: 15, max: 200)
 * @param {string}            [props.sortBy]         - Sort field: 'date' | 'relevance' (default: 'relevance')
 * @param {string}            [props.sortOrder]      - Sort direction: 'asc' | 'desc' (default: 'desc')
 * @param {string|null}       [props.filterBy]       - 'case' | 'lead' | null for both (default: null)
 * @param {boolean}           [props.includeClosed]  - Include closed cases in results (default: false)
 * @param {Object}            [props.styles]         - Custom react-select style overrides
 * @param {string}            [props.name]
 * @param {string}            [props.id]
 * @param {Function}          [props.onBlur]
 * @param {Function}          [props.onFocus]
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { components as selectComponents } from 'react-select';
import { AsyncPaginate, wrapMenuList } from 'react-select-async-paginate';
import { loadCaseSearchOptions } from 'hooks/queries/useCaseSearchDropdown';
import '../shared/AsyncSelect.scss';
import './CaseSelect.scss';

// ── Custom Option ─────────────────────────────────────────────────────────────

function CaseOption({ innerRef, innerProps, isSelected, isFocused, data, children }) {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="case-select-option">
        <div className="case-select-option__row">
          {/* Checkbox affordance when rendered inside a multi-select */}
          {data.__isMulti && (
            <span className={`case-select-checkbox${isSelected ? ' case-select-checkbox--checked' : ''}`} aria-hidden="true" />
          )}
          <span className="case-select-option__name" title={data.case_name}>
            {children}
          </span>
          {data.is_lead && <span className="case-select-badge case-select-badge--lead">Lead</span>}
          {data.case_closed && <span className="case-select-badge case-select-badge--closed">Closed</span>}
        </div>
        {data.subtitle && (
          <div className="case-select-option__meta">
            <span className="case-select-option__subtitle" title={data.subtitle}>
              {data.subtitle}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Custom MenuList: renders children + a bottom spinner during pagination ────

function CaseMenuListBase({ children, selectProps, ...props }) {
  const isPaginating = selectProps.isLoading && selectProps.options?.length > 0;
  return (
    <selectComponents.MenuList {...props}>
      {children}
      {isPaginating && (
        <div className="case-select-menu-loader">
          <span className="case-select-menu-loader__spinner" aria-hidden="true" />
          <span>Loading more...</span>
        </div>
      )}
    </selectComponents.MenuList>
  );
}

// wrapMenuList restores AsyncPaginate's scroll-to-load detection on the custom MenuList.
const CaseMenuList = wrapMenuList(CaseMenuListBase);

// ── Component ─────────────────────────────────────────────────────────────────

function CaseSelect({
  value = null,
  onChange,
  placeholder = 'Search cases...',
  isClearable = true,
  isDisabled = false,
  isMulti = false,
  className = '',
  pageSize = 15,
  sortBy = 'relevance',
  sortOrder = 'desc',
  filterBy = null,
  includeClosed = false,
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  const loadOptions = useMemo(
    () => (search, loadedOptions, additional) =>
      loadCaseSearchOptions(search, loadedOptions, additional, {
        pageSize,
        sortBy,
        sortOrder,
        filterBy,
        includeClosed,
      }),
    [pageSize, sortBy, sortOrder, filterBy, includeClosed]
  );

  // Inject __isMulti so the custom option renderer can conditionally show checkboxes.
  const formatOptionLabel = useMemo(() => (option) => option.label, []);

  const components = useMemo(
    () => ({
      Option: (props) => <CaseOption {...props} data={{ ...props.data, __isMulti: isMulti }} />,
      MenuList: CaseMenuList,
    }),
    [isMulti]
  );

  const mergedStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        minHeight: '38px',
        ...(isMulti
          ? {
              maxHeight: '38px',
              flexWrap: 'nowrap',
              overflow: 'hidden',
            }
          : {}),
      }),
      valueContainer: (base) => ({
        ...base,
        ...(isMulti
          ? {
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }
          : {}),
      }),
      multiValue: (base) => ({
        ...base,
        flexShrink: 0,
        minWidth: 'fit-content',
      }),
      option: (base) => ({
        ...base,
        padding: '6px 8px',
      }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      ...styles,
    }),
    [isMulti, styles]
  );

  return (
    <AsyncPaginate
      value={value}
      loadOptions={loadOptions}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable
      isMulti={isMulti}
      hideSelectedOptions={false}
      closeMenuOnSelect={!isMulti}
      className={`case-select-dropdown ${className}`.trim()}
      classNamePrefix="async-select-custom"
      styles={mergedStyles}
      components={components}
      formatOptionLabel={formatOptionLabel}
      additional={{ page: 1 }}
      cacheUniqs={[sortBy, sortOrder, filterBy, includeClosed]}
      debounceTimeout={300}
      name={name}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      loadingMessage={() => 'Searching cases...'}
      noOptionsMessage={({ inputValue }) => (inputValue ? `No cases found for "${inputValue}"` : 'Type to search cases')}
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={(e) => e.target === document}
      {...rest}
    />
  );
}

CaseSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  className: PropTypes.string,
  /** Records fetched per page (1–200, API default: 15) */
  pageSize: PropTypes.number,
  /** Sort field: 'date' sorts by created_at; 'relevance' sorts by OpenSearch _score */
  sortBy: PropTypes.oneOf(['date', 'relevance']),
  /** Sort direction */
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  /** Filter by resource type: 'case' | 'lead' | null for both */
  filterBy: PropTypes.oneOf(['case', 'lead', null]),
  /** When true, closed cases are included in search results */
  includeClosed: PropTypes.bool,
  styles: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default CaseSelect;
